import React, { useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "../../design/design-system";
import SearchBar from "../../atoms/SearchBar";
import Button from "../../atoms/Button";
import Text from "../../atoms/Text";

export interface ContentSearchBarProps {
  targetElement: HTMLElement | null;
  placeholder?: string;
  onSearchStateChange?: (isSearching: boolean, searchTerm: string) => void;
  className?: string;
  style?: React.CSSProperties;
  /** 검색 행 오른쪽 끝에 함께 배치할 요소(예: 검색범위 필터). 한 행으로 묶어 세로 공간 절약 */
  rightSlot?: React.ReactNode;
}

const SearchContainer = styled.div`
  /* 좌우 패딩·배경 띠 제거 — 아래 콘텐츠(좌우 패딩 0)와 좌측 정렬을 맞춘다. 위아래만 간격. */
  padding: 8px 0;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SearchBarWrapper = styled.div`
  flex: 1;
  min-width: 0;
  /* SearchBar atom Wrapper(min-width:120px)를 눌러, 카운트·네비·필터를 넣고 남은 폭만큼만 차지하게 함 */
  & > div {
    min-width: 0;
  }
`;

const SearchResults = styled.div`
  flex-shrink: 0;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  white-space: nowrap;
  margin-right: 8px;
`;

const NavigationButtonGroup = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 4px;
`;

const ContentSearchBar: React.FC<ContentSearchBarProps> = ({
  targetElement,
  placeholder = "내용에서 검색...",
  onSearchStateChange,
  className,
  style,
  rightSlot,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchResults, setSearchResults] = useState<number>(0);
  const [currentResultIndex, setCurrentResultIndex] = useState<number>(-1);

  // 검색 기능
  const performSearch = useCallback((term: string) => {
    if (!targetElement || !term.trim()) {
      clearSearch();
      return;
    }

    // 이전 검색 하이라이트 제거
    clearSearchHighlights();

    const searchRegex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const walker = document.createTreeWalker(
      targetElement,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node;

    while (node = walker.nextNode()) {
      if (node.textContent && searchRegex.test(node.textContent)) {
        textNodes.push(node as Text);
      }
    }

    let totalMatches = 0;
    textNodes.forEach(textNode => {
      const parent = textNode.parentNode;
      if (parent && parent instanceof Element && !parent.closest('.search-highlight')) {
        const text = textNode.textContent || '';
        const highlightedHTML = text.replace(searchRegex, (match) => {
          totalMatches++;
          return `<span class="search-highlight" data-search-index="${totalMatches - 1}">${match}</span>`;
        });
        
        if (highlightedHTML !== text) {
          const wrapper = document.createElement('span');
          wrapper.innerHTML = highlightedHTML;
          parent.replaceChild(wrapper, textNode);
        }
      }
    });

    setSearchResults(totalMatches);
    setCurrentResultIndex(totalMatches > 0 ? 0 : -1);

    // 첫 번째 결과로 스크롤
    if (totalMatches > 0) {
      scrollToSearchResult(0);
    }

    // 부모 컴포넌트에 검색 상태 알림
    onSearchStateChange?.(totalMatches > 0, term);
  }, [targetElement, onSearchStateChange]);

  const clearSearch = useCallback(() => {
    clearSearchHighlights();
    setSearchResults(0);
    setCurrentResultIndex(-1);
    onSearchStateChange?.(false, "");
  }, [onSearchStateChange]);

  const clearSearchHighlights = useCallback(() => {
    if (targetElement) {
      const highlightedElements = targetElement.querySelectorAll('.search-highlight');
      highlightedElements.forEach(element => {
        const parent = element.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(element.textContent || ''), element);
          parent.normalize();
        }
      });
    }
  }, [targetElement]);

  const scrollToSearchResult = useCallback((index: number) => {
    if (!targetElement) return;

    // 이전 current 클래스 제거
    const prevCurrent = targetElement.querySelector('.search-highlight.current');
    if (prevCurrent) {
      prevCurrent.classList.remove('current');
    }

    // 새로운 current 설정
    const targetElementToScroll = targetElement.querySelector(`[data-search-index="${index}"]`);
    if (targetElementToScroll) {
      targetElementToScroll.classList.add('current');
      targetElementToScroll.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [targetElement]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    // performSearch 호출 제거 - useEffect에서 디바운싱 후 처리
  };

  const handleSearchNavigation = (direction: 'prev' | 'next') => {
    if (searchResults === 0) return;

    let newIndex;
    if (direction === 'next') {
      newIndex = currentResultIndex >= searchResults - 1 ? 0 : currentResultIndex + 1;
    } else {
      newIndex = currentResultIndex <= 0 ? searchResults - 1 : currentResultIndex - 1;
    }

    setCurrentResultIndex(newIndex);
    scrollToSearchResult(newIndex);
  };

  // Enter=다음 매칭, Shift+Enter=이전 매칭 (워드 찾기 동작).
  // 한글 IME 조합 확정 Enter(isComposing)는 무시해 약품명 입력 중 오작동 방지.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    if (searchResults === 0) return;
    e.preventDefault();
    handleSearchNavigation(e.shiftKey ? "prev" : "next");
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    clearSearch();
  };

  // 검색어 디바운싱
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms 디바운스

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // 디바운싱된 검색어로 검색 실행
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      performSearch(debouncedSearchTerm);
    } else if (debouncedSearchTerm === "") {
      clearSearch();
    }
  }, [targetElement, debouncedSearchTerm]); // performSearch 의존성 제거

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      clearSearchHighlights();
    };
  }, [clearSearchHighlights]);

  return (
    <SearchContainer className={className} style={style}>
      <SearchBarWrapper>
        <SearchBar
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          onClear={handleClearSearch}
        />
      </SearchBarWrapper>
      
      {searchResults > 0 && (
        <>
          <SearchResults>
            <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[600]}>
              {currentResultIndex + 1} / {searchResults}
            </Text>
          </SearchResults>
          <NavigationButtonGroup>
            <Button
              type="Icon"
              variant="Ghost"
              color="Secondary"
              size="Small"
              icon="Arrow Up"
              onClick={() => handleSearchNavigation('prev')}
              disabled={searchResults === 0}
            />
            <Button
              type="Icon"
              variant="Ghost"
              color="Secondary"
              size="Small"
              icon="Arrow Down"
              onClick={() => handleSearchNavigation('next')}
              disabled={searchResults === 0}
            />
          </NavigationButtonGroup>
        </>
      )}
      {rightSlot}
    </SearchContainer>
  );
};

export default ContentSearchBar; 